/**
 * Error Boundary Component
 * Catches React errors and displays fallback UI
 * @module components/ErrorBoundary
 */

import React from 'react';
import { LanguageContext } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { handleAdvancedError } from '@/utils/advancedErrorHandler';
import { trackError } from '@/utils/monitoring';

const CHUNK_RELOAD_KEY = 'teamin_chunk_reload';

function getNested(obj, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] != null ? acc[key] : null), obj);
}

function translate(language, path) {
  const langs = [language, 'en', 'ar', 'de'];
  for (const lang of langs) {
    const value = getNested(translations[lang], path);
    if (typeof value === 'string') return value;
  }
  return path;
}

function isChunkLoadError(error) {
  if (!error) return false;
  const message = String(error.message || error);
  const name = String(error.name || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

function getReloadStorageKey() {
  try {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    return `${CHUNK_RELOAD_KEY}:${path || '/'}`;
  } catch (_) {
    return CHUNK_RELOAD_KEY;
  }
}

class ErrorBoundary extends React.Component {
  static contextType = LanguageContext;

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this._successClearTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught in ErrorBoundary:', error, errorInfo);

    if (this._successClearTimer) {
      clearTimeout(this._successClearTimer);
      this._successClearTimer = null;
    }

    this.setState({
      error,
      errorInfo
    });

    // Stale/missing JS chunks after deploy: ONE automatic reload per path.
    // Do NOT clear the flag while Suspense is still loading — that caused infinite reload loops.
    if (isChunkLoadError(error)) {
      try {
        const storageKey = getReloadStorageKey();
        const alreadyReloaded = sessionStorage.getItem(storageKey);
        if (!alreadyReloaded) {
          sessionStorage.setItem(storageKey, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch (_) {
        // sessionStorage may be unavailable — fall through to error UI
      }
    }

    // Advanced error handling
    try {
      const errorData = handleAdvancedError(error, {
        showNotification: false,
        logError: true,
        trackError: true,
        context: {
          componentStack: errorInfo.componentStack,
          errorBoundary: true,
        },
      });

      trackError(error, {
        errorInfo,
        componentStack: errorInfo.componentStack,
        errorType: errorData.type,
      });
    } catch (trackingError) {
      console.error('Failed to track error:', trackingError);
    }
  }

  componentDidMount() {
    if (!this.state.hasError) {
      this.scheduleClearReloadFlag();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { resetKeys } = this.props;
    if (this.state.hasError && resetKeys) {
      const prevKeys = prevProps.resetKeys || [];
      const changed =
        resetKeys.length !== prevKeys.length ||
        resetKeys.some((key, i) => key !== prevKeys[i]);

      if (changed) {
        this.resetError();
        return;
      }
    }

    // Tree recovered from error — allow a future one-shot reload later
    if (!this.state.hasError && prevState.hasError) {
      this.scheduleClearReloadFlag();
    }
  }

  componentWillUnmount() {
    if (this._successClearTimer) {
      clearTimeout(this._successClearTimer);
      this._successClearTimer = null;
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  scheduleClearReloadFlag = () => {
    // Only clear after the tree has stayed healthy for a few seconds.
    // Clearing immediately on Suspense fallback caused: load → clear → fail → reload → loop.
    if (this._successClearTimer) {
      clearTimeout(this._successClearTimer);
    }
    this._successClearTimer = setTimeout(() => {
      try {
        sessionStorage.removeItem(getReloadStorageKey());
      } catch (_) {
        // ignore
      }
      this._successClearTimer = null;
    }, 8000);
  };

  render() {
    if (this.state.hasError) {
      const error = this.state.error;
      const errorType = error?.response?.status || 'UNKNOWN';
      const isNetworkError = !navigator.onLine || errorType === 'NETWORK_ERROR';
      const isAuthError = errorType === 401 || errorType === 403;
      const isChunkError = isChunkLoadError(error);
      const language = this.context?.language || 'ar';
      const t = (key) => translate(language, key);

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="max-w-md w-full text-center">
            <div className="mb-6">
              {isNetworkError || isChunkError ? (
                <div className="text-6xl mb-4">📡</div>
              ) : isAuthError ? (
                <div className="text-6xl mb-4">🔒</div>
              ) : (
                <div className="text-6xl mb-4">⚠️</div>
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {isNetworkError || isChunkError
                ? t('errorBoundary.connectionIssue')
                : isAuthError
                ? t('errorBoundary.sessionExpired')
                : t('errorBoundary.somethingWentWrong')}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {isChunkError
                ? t('errorBoundary.chunkError')
                : isNetworkError
                ? t('errorBoundary.networkError')
                : isAuthError
                ? t('errorBoundary.authError')
                : t('errorBoundary.unexpectedError')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {!isAuthError && (
                <button
                  onClick={this.resetError}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('common.retry')}
                </button>
              )}
              {isAuthError && (
                <button
                  onClick={() => {
                    localStorage.clear();
                    window.location.href = '/login';
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('common.login')}
                </button>
              )}
              <button
                onClick={() => {
                  try {
                    sessionStorage.removeItem(getReloadStorageKey());
                  } catch (_) {
                    // ignore
                  }
                  window.location.reload();
                }}
                className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.reloadPage')}
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-8 text-left max-w-2xl mx-auto">
                <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {t('errorBoundary.errorDetails')}
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-auto max-h-64">
                  {error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
